import Omy from "../Omy";
import OMap from "../utils/OMap";
import {OMY_SETTING, OmyConst} from "../OmyConst";
import {Howler} from "howler";
import OMath from "../utils/OMath";

export default class OSound {
    constructor() {
        this._volume = 1;
        this._fxVolume = 1;
        this._musicVolume = 1;
        this._isMute = false;
        this._isMuteSound = false;
        this._isMuteMusic = false;
        this._isPauseAll = false;
        this._soundList = [];
        this._musicList = [];

        this._emit = new OmyConst.Emit();

        this._map = new OMap();
        Howler.autoSuspend = false;

        this._soundHasAccess = false;
        this._need2PlayAfterAccess = [];
        this._soundsVolumeMap = {};
    }

    /** @public */
    add2LibSprite(name, sound, audioNames) {
        this._sprite = sound;
        this._audioSpriteNames = audioNames;
        this._spriteIdData = {};
        for (let i = 0; i < audioNames.length; i++) {
            this._soundList.push(audioNames[i]);
            if (!this._map.has(audioNames[i]))
                this._map.set(audioNames[i], {loop: false});
        }

        this._sprite.on("end", this._onEndSpriteAudio.bind(this));
        this._sprite.on("stop", this._onStopSpriteAudio.bind(this));
        this._sprite.once("unlock", this._allowSounds.bind(this));

        if (Omy.isRealDesktop && Omy.browser.safari) {
            if (this._map.has("_mac_fix")) {
                this.play("_mac_fix", true);
            }
        }
    }

    /** @private */
    _onEndSpriteAudio(id) {
        const sound = this._spriteIdData[String(id)];
        if (sound) {
            if (!sound._loop) {
                this._spriteIdData[String(id)] = null;
                delete this._spriteIdData[String(id)];
                if (this._map.get(sound.sName).loop) {
                    this.play(sound.sName, true);
                }
            }
        } else {
            Omy.warn("sound not in lib");
        }
    }

    /** @private */
    _onStopSpriteAudio(id) {
        const sound = this._spriteIdData[String(id)];
        if (sound) {
            this._spriteIdData[String(id)] = null;
            delete this._spriteIdData[String(id)];
        } else {
            Omy.warn("sound not in lib");
        }
    }

    /** @private */
    _inAudioSprite(sName) {
        return this._sprite && OMath.inArray(this._audioSpriteNames, sName);
    }

    /** @private */
    _getAllIds(sName) {
        const result = [];
        for (let id in this._spriteIdData) {
            if (this._spriteIdData[id].sName === sName)
                result.push(Number(id));
        }
        return result;
    }

    /**     * @public     */
    add2Lib(type, name, sound) {
        OMY.Omy.warn("add2Lib", type, name, sound);
        if (type === "music")
            this._musicList.push(name);

        sound.omyPaused = false;
        if (!this._map.has(name))
            this._map.set(name, sound);
    }

    /**
     * @public
     * @param {array}namesList
     */
    addMusicList(namesList) {
        this._musicList = namesList;
    }

    pauseAll() {
        if (!this._soundHasAccess) return null;
        this._isPauseAll = true;
        let names = this._map.names();
        let sound;
        for (let i = 0; i < names.length; i++) {
            if (this._inAudioSprite(names[i])) {
                const ids = this._getAllIds(names[i]);
                for (let j = 0; j < ids.length; j++) {
                    sound = this._spriteIdData[String(ids[j])];
                    this._sprite.pause(ids[j]);
                }
                if (sound && !sound.omyPaused) {
                    sound.omyPaused = true;
                }
            } else {
                sound = this._map.get(names[i]);
                if (sound.playing()) {
                    sound.omyPaused = true;
                    sound.pause();
                }
            }
        }
    }

    resumeAll() {
        if (!this._soundHasAccess) return null;
        this._isPauseAll = false;
        let names = this._map.names();
        let sound;
        for (let i = 0; i < names.length; i++) {
            if (this._inAudioSprite(names[i])) {
                const ids = this._getAllIds(names[i]);
                for (let j = 0; j < ids.length; j++) {
                    sound = this._spriteIdData[String(ids[j])];
                    this._sprite.play(ids[j]);
                }
                if (sound && sound.omyPaused) {
                    sound.omyPaused = false;
                }
            } else {
                sound = this._map.get(names[i]);
                if (sound.omyPaused) {
                    sound.omyPaused = false;
                    sound.play();
                }
            }
        }
    }

    play(sName, loop = false, forceRestart = false, volume = 1) {
        if (!sName) return;
        if (!this._soundHasAccess) {
            loop && this._need2PlayAfterAccess.push([sName, loop, forceRestart, volume]);
            return null;
        }
        if (Omy.debugMode && OMY_SETTING.VERBOSE_BUTTONS_STATE)
            Omy.warn("play sound: ", sName);
        if (!this._map.has(sName)) {
            Omy.warn("Missed sound: " + sName);
            return null;
        }
        let s = null;
        if (this._inAudioSprite(sName)) {
            s = this._sprite;
            const id = this._sprite.play(sName);
            const sound = this._sprite._soundById(id);
            if (!sound) {
                return sound;
            }
            sound.sName = sName;
            this._spriteIdData[String(id)] = sound;

            if (forceRestart) {
                const ids = this._getAllIds(sName);
                for (let i = 0; i < ids.length; i++) {
                    this._sprite.stop(ids[i]);
                }
            }

            this._sprite.loop(loop, id);
            this._map.get(sName).loop = loop;
            this._sprite.volume(volume * ((this.isMusic(sName)) ? this._musicVolume : this._fxVolume), id);
            sound.defVolume = volume;
            if (!this._isMute && this._isMuteMusic && this.isMusic(sName)) this._sprite.mute(true, id);
            else if (!this._isMute && this._isMuteSound) this._sprite.mute(true, id);

            if (this._isPauseAll) {
                sound.omyPaused = true;
                this._sprite.pause(id);
            }
            return sound;
        } else {
            s = this._map.get(sName);
            s.loop(loop);
            if (forceRestart) {
                s.stop();
            }
            s.volume(volume * ((this.isMusic(sName)) ? this._musicVolume : this._fxVolume));
            s.defVolume = volume;

            s.play();
            if (this._isPauseAll) {
                s.omyPaused = true;
                s.pause();
            }
            return s;
        }
        return s;
    }

    playById(id) {
        if (!this._soundHasAccess) return;
        if (this._sprite) {
            this._sprite.play(id);
        } else {
            let names = this._map.names();
            let sound;
            for (let i = 0; i < names.length; i++) {
                sound = this._map.get(names[i]);
                if (sound._soundById(id)) {
                    this._map.get(names[i]).play(id);
                }
            }
        }
    }

    stop(sName) {
        if (!this._soundHasAccess) {
            for (let i = 0; i < this._need2PlayAfterAccess.length; i++) {
                if (this._need2PlayAfterAccess[i][0] === sName) {
                    this._need2PlayAfterAccess.splice(i, 1);
                    break;
                }
            }
            return null;
        }
        if (Omy.debugMode && OMY_SETTING.VERBOSE_BUTTONS_STATE)
            Omy.warn("stop sound, sName", sName);
        if (!this._map.has(sName)) {
            Omy.warn("stop error, sName", sName);
            return null;
        }
        if (this._inAudioSprite(sName)) {
            const ids = this._getAllIds(sName);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.stop(ids[i]);
            }
        } else {
            this._map.get(sName).stop();
        }
    }

    stopById(id) {
        if (!this._soundHasAccess) return;
        if (this._sprite) {
            this._sprite.stop(id);
        } else {
            let names = this._map.names();
            let sound;
            for (let i = 0; i < names.length; i++) {
                sound = this._map.get(names[i]);
                if (sound._soundById(id)) {
                    this._map.get(names[i]).stop(id);
                }
            }
        }
    }

    pause(sName) {
        if (!this._soundHasAccess) return;
        if (!this._map.has(sName)) {
            return null;
        }
        if (Omy.debugMode && OMY_SETTING.VERBOSE_BUTTONS_STATE)
            Omy.warn("pause sound: ", sName);
        if (this._inAudioSprite(sName)) {
            const ids = this._getAllIds(sName);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.pause(ids[i]);
            }
        } else {
            this._map.get(sName).pause();
        }
    }

    resume(sName) {
        if (!this._soundHasAccess) return;
        if (!this._map.has(sName)) {
            return null;
        }
        if (Omy.debugMode && OMY_SETTING.VERBOSE_BUTTONS_STATE)
            Omy.warn("resume sound: ", sName);
        if (this._inAudioSprite(sName)) {
            const ids = this._getAllIds(sName);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.play(ids[i]);
            }
        } else {
            this._map.get(sName).play();
        }
    }

    fadeIn(sName, sec) {
        this.fade(sName, sec, 0, 1);
    }

    fadeOut(sName, sec) {
        this.fade(sName, sec, 1, 0);
    }

    fadeTo(sName, sec, volume) {
        if (!this._map.has(sName)) return null;
        this._soundsVolumeMap[sName] = volume;
        if (!this._soundHasAccess) return null;

        if (this._inAudioSprite(sName)) {
            const ids = this._getAllIds(sName);
            if (ids.length) {
                this.fade(sName, sec, this._spriteIdData[String(ids[0])]._volume, volume * ((this.isMusic(sName)) ? this._musicVolume : this._fxVolume));
                this._spriteIdData[String(ids[0])].defVolume = volume;
            } else {
                Omy.warn("cannot find active sound id");
            }
        } else {
            this.fade(sName, sec, this._map.get(sName).volume(), volume);
        }
    }

    fade(sName, sec, fromVolume, toVolume) {
        if (!this._map.has(sName)) return null;
        this._soundsVolumeMap[sName] = toVolume;
        if (!this._soundHasAccess) return null;

        if (Omy.debugMode && OMY_SETTING.VERBOSE_BUTTONS_STATE)
            Omy.warn("fade sound: ", sName, sec, fromVolume, toVolume);
        if (this._inAudioSprite(sName)) {
            const ids = this._getAllIds(sName);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.fade(fromVolume, toVolume, sec * 1000.0, ids[i]);
            }
        } else {
            this._map.get(sName).fade(fromVolume, toVolume, sec * 1000.0);
        }
    }

    getSound(sName) {
        if (!this._map.has(sName)) return null;
        if (!this._soundHasAccess) return;
        if (this._sprite && OMath.inArray(this._audioSpriteNames, sName))
            return this._getAllIds(sName);
        else
            return this._map.get(sName);
    }

    getSoundVolume(sName) {
        if (!this._map.has(sName)) return null;
        if (!this._soundHasAccess) return;
        if (this._sprite && OMath.inArray(this._audioSpriteNames, sName)) {
            let volume = 0;
            let count = 0;
            const ids = this._getAllIds(sName);
            for (let i = 0; i < ids.length; i++) {
                const sound = this._sprite._soundById(ids[i]);
                if (!sound) continue;
                count++;
                volume += sound._volume;
            }
            return volume / count;
        } else {
            return this._map.get(sName).volume();
        }
    }

    isSoundPlay(sName) {
        if (!this._map.has(sName)) return false;

        if (this._inAudioSprite(sName)) {
            const ids = this._getAllIds(sName);
            return Boolean(ids.length);
        } else {
            return this._map.get(sName).playing();
        }
    }

    isIdPlay(id) {
        return this._sprite.playing(id);
    }

    volume(value) {
        if (value < 0 || value > 1) {
            Omy.log("no correct volume!" + value);
            return;
        }
        this._volume = value;
        Howler.volume(value);
    }

    mute() {
        this._isMuteSound = true;
        this._isMuteMusic = true;
        this._isMute = true;
        Howler.mute(true);
    }

    unmute() {
        this._isMuteSound = false;
        this._isMuteMusic = false;
        this._isMute = false;
        Howler.mute(false);
    }

    toggleMute() {
        if (this._isMute)
            this.unmute();
        else
            this.mute();
    }

    toggleSoundMute() {
        this._isMuteSound = !this._isMuteSound;

        for (let i = 0; i < this._soundList.length; i++) {
            if (this.isMusic(this._soundList[i])) continue;
            if (this._inAudioSprite(this._soundList[i])) {
                const ids = this._getAllIds(this._soundList[i]);
                for (let i = 0; i < ids.length; i++) {
                    this._sprite.mute(this._isMuteSound, ids[i]);
                }
            } else {
                this.getSound(this._soundList[i]).mute(this._isMuteSound);
            }
        }

    }

    toggleMusicMute() {
        this._isMuteMusic = !this._isMuteMusic;

        for (let i = 0; i < this._musicList.length; i++) {
            if (this._inAudioSprite(this._musicList[i])) {
                const ids = this._getAllIds(this._musicList[i]);
                for (let i = 0; i < ids.length; i++) {
                    this._sprite.mute(this._isMuteMusic, ids[i]);
                }
            } else {
                this.getSound(this._musicList[i]).mute(this._isMuteMusic);
            }
        }
    }

    isMusic(sName) {
        if (!this._musicList.length) return false;
        return OMath.inArray(this._musicList, sName);
    }

    /**     * @public     */
    addPlayEndEvent(s_name, f, c, once = false) {
        this._addEvent(s_name, "end", f, c, once);
    }

    /**     * @public     */
    removePlayEndEvent(s_name, f) {
        if (!this._map.has(s_name)) {
            Omy.error("cannot find this sound:", s_name);
            return false;
        }
        if (this._inAudioSprite(s_name)) {
            const ids = this._getAllIds(s_name);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.off("end", f, ids[i]);
            }
        } else {
            this._map.get(s_name).off("end", f);
        }
    }

    /**     * @public     */
    addPlayEvent(s_name, f, c, once = false) {
        this._addEvent(s_name, "play", f, c, once);
    }

    /**     * @public     */
    removePlayEvent(s_name, f) {
        if (!this._map.has(s_name)) {
            Omy.error("cannot find this sound:", s_name);
            return false;
        }
        if (this._inAudioSprite(s_name)) {
            const ids = this._getAllIds(s_name);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.off("play", f, ids[i]);
            }
        } else {
            this._map.get(s_name).off("play", f);
        }
    }

    /**     * @public     */
    addPauseEvent(s_name, f, c, once = false) {
        this._addEvent(s_name, "pause", f, c, once);
    }

    /**     * @public     */
    removePauseEvent(s_name, f) {
        if (!this._map.has(s_name)) {
            Omy.error("cannot find this sound:", s_name);
            return false;
        }
        if (this._inAudioSprite(s_name)) {
            const ids = this._getAllIds(s_name);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.off("pause", f, ids[i]);
            }
        } else {
            this._map.get(s_name).off("pause", f);
        }
    }

    /**     * @public     */
    addStopEvent(s_name, f, c, once = false) {
        this._addEvent(s_name, "stop", f, c, once);
    }

    /**     * @public     */
    removeStopEvent(s_name, f) {
        if (!this._map.has(s_name)) {
            Omy.error("cannot find this sound:", s_name);
            return false;
        }
        if (this._inAudioSprite(s_name)) {
            const ids = this._getAllIds(s_name);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.off("stop", f, ids[i]);
            }
        } else {
            this._map.get(s_name).off("stop", f);
        }
    }

    /**     * @public     */
    addFadeEvent(s_name, f, c, once = false) {
        this._addEvent(s_name, "fade", f, c, once);
    }

    /**     * @public     */
    removeFadeEvent(s_name, f) {
        if (!this._map.has(s_name)) {
            Omy.error("cannot find this sound:", s_name);
            return false;
        }
        if (this._inAudioSprite(s_name)) {
            const ids = this._getAllIds(s_name);
            for (let i = 0; i < ids.length; i++) {
                this._sprite.off("fade", f, ids[i]);
            }
        } else {
            this._map.get(s_name).off("fade", f);
        }
    }

    isSoundExist(sName) {
        let result = false;
        if (this._inAudioSprite(sName)) result = true;
        else if (this._map.has(sName)) result = true;
        return result;
    }

    allowSounds() {
        console.warn("It`s old method. Remove it calls");
    }

    _allowSounds() {
        this._soundHasAccess = true;
        let listStopped = this._need2PlayAfterAccess;
        for (let i = 0; i < listStopped.length; i++) {
            let name = listStopped[i][0];
            let volume = (this._soundsVolumeMap[name] === undefined) ?
                (listStopped[i][3] === undefined) ? 1 : listStopped[i][3] : this._soundsVolumeMap[name];
            this.play(name, listStopped[i][1], listStopped[i][2], volume);
            // volume !== 1 && this.fadeTo(name, 0.1, volume);
        }
        this._need2PlayAfterAccess = null;
    }

//---------------------------------------
// PRIVATE
//---------------------------------------

    /**     * @private     */
    _addEvent(s_name, e_name, f, c, once = true) {
        if (!this._map.has(s_name)) {
            Omy.error("cannot find this sound:", s_name);
            return false;
        }
        if (this._inAudioSprite(s_name)) {
            const ids = this._getAllIds(s_name);
            for (let i = 0; i < ids.length; i++) {
                if (once)
                    this._sprite.once(e_name, f.bind(c), ids[i]);
                else
                    this._sprite.on(e_name, f.bind(c), ids[i]);
            }
        } else {
            if (once)
                this._map.get(s_name).once(e_name, f.bind(c));
            else
                this._map.get(s_name).on(e_name, f.bind(c));
        }
    }

//---------------------------------------
// ACCESSOR
//---------------------------------------

    get emit() {
        return this._emit;
    }

    get isMute() {
        return this._isMute;
    }

    get isMuteSound() {
        return this._isMuteSound;
    }

    get isMuteMusic() {
        return this._isMuteMusic;
    }

    get curVolume() {
        return this._volume;
    }
}
